import React, { useEffect } from 'react';
import styled from '@emotion/styled';

import { Box, Divider as MuiDivider, Link, Paper as MuiPaper, Stack, Typography, TypographyProps } from '@mui/material';
import { spacing } from '@mui/system';
import Loader from '../../components/common/Loader';

import { useCompanyStore } from '../../stores';
import { CompanyInfoResponse } from '../../apis/companyApi';
import PageHeader from '../../components/common/PageHeader';

const Divider = styled(MuiDivider)(spacing);
const Paper = styled(MuiPaper)(spacing);

const Heading: React.FC<TypographyProps> = (props: TypographyProps) => {
  return <Typography variant="h4" mt={6} mb={2} ml={2} {...props}></Typography>;
};

type ContactInfoProps = {
  contactInfo: CompanyInfoResponse;
};

const ContactInfo: React.FC<ContactInfoProps> = ({ contactInfo }) => {
  return (
    <Paper mt={4} pt={0} px={6} pb={6} sx={{ minWidth: 300 }}>
      {contactInfo.logoUrl && (
        <Box
          component="img"
          src={contactInfo.logoUrl}
          title="Company Logo"
          sx={{
            marginTop: 6,
            marginBottom: 1,
            width: 300,
            maxWidth: { xs: 250, md: 300 },
          }}
        ></Box>
      )}
      <Stack spacing={3}>
        <Box>
          <Heading variant="h4">Address</Heading>
          <Divider mb={3}></Divider>
          <Box px={2}>
            <Typography>{contactInfo.address}</Typography>
            <Typography>
              {contactInfo.city}, {contactInfo.state} {contactInfo.zip}
            </Typography>
          </Box>
        </Box>
        <Box>
          <Heading variant="h4">Phone</Heading>
          <Divider mb={3}></Divider>
          <Box px={2}>
            <Typography>{contactInfo.phone}</Typography>
          </Box>
        </Box>
        <Box>
          <Heading variant="h4">Email</Heading>
          <Divider mb={3}></Divider>
          <Box px={2}>
            <Link href={`mailto:${contactInfo.email}`}>{contactInfo.email}</Link>
          </Box>
        </Box>
        {contactInfo.website && (
          <Box>
            <Heading variant="h4">Website</Heading>
            <Divider mb={3}></Divider>
            <Box px={2}>
              <Link target="_blank" href={contactInfo.website.startsWith('http') ? contactInfo.website : `https://${contactInfo.website}`}>
                {contactInfo.website}
              </Link>
            </Box>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

function ContactUs() {
  const getCompanyInfo = useCompanyStore.use.getCompanyInfo();
  const companyInfo = useCompanyStore.use.companyInfo();

  useEffect(() => {
    getCompanyInfo();
  }, [getCompanyInfo]);

  return (
    <>
      {!companyInfo && <Loader></Loader>}
      {companyInfo && (
        <>
          <PageHeader title="Contact Us" heading={companyInfo.name} />
          <ContactInfo contactInfo={companyInfo} />
        </>
      )}
    </>
  );
}

export default ContactUs;
